import { DestroyRef } from '@angular/core';
import type { GridOrchestrationDataAccess } from './grid-orchestration-data-access';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY, Observable, catchError, of, switchMap, take, tap } from 'rxjs';
import {
  GridRowsPage,
  GridBucketRowsPage,
  GridBucketScope,
  type GridSortField
} from '../contracts/public-api';
import type { GridBackendAttributeFilter } from '../d-data-grid/column-filter.types';
import { isNullBucketPath, resolveExistingBucketKey } from './grid-orchestration-bucket-path';
import { asGridSourceRow } from './grid-source-row.interface';
import {
  computeGlobalHasMoreFromPagination,
  computeNextBucketStateAfterFetch,
  createBucketSkeletonState,
  immutableUpdateGroupPaginationState,
  isBucketFirstPagePending,
  isBucketLoadable,
  type MergedPaginationElements,
  mergeGroupPaginationIntoElementsData,
  recordBucketTotalAsSubGroupCount,
  resolveExpectedBucketTotal
} from './grid-orchestration-pagination-state';
import { isStaleQueryGeneration } from './grid-orchestration-query-generation';
import { BUCKET_PAGE_SIZE, GROUP_PATH_DELIMITER } from './grid-orchestration.constants';
import { GridOrchestrationLogLevel, logGridOrchestration } from './grid-orchestration.dev-log';
import type { GroupPaginationState } from './grid-orchestration.types';
import { BATCH_BUCKET_SIZE, BATCH_FLUSH_MS } from './grid-orchestration.utils';

/** Host callbacks/state accessors used by pagination helpers. */
export interface GridPaginationHost {
  readonly elementsProvider: GridOrchestrationDataAccess;
  readonly destroyRef: DestroyRef;
  getQueryGeneration: () => number;
  getGridEndCursor: () => string | null;
  setGridEndCursor: (cursor: string | null) => void;
  getGridLoadingMore: () => boolean;
  beginGridLoadingMore: () => void;
  endGridLoadingMore: () => void;
  scheduleBatchFallbackLoad: (path: string, delayMs: number, queryGeneration: number) => void;
  setGridHasMore: (hasMore: boolean) => void;
  setElementsError: (message: string | null) => void;
  getElements: () => GridRowsPage | null;
  setElements: (response: GridRowsPage) => void;
  getLastGridWhere: () => Record<string, unknown>;
  getGroupPagination: () => Map<string, GroupPaginationState> | null;
  setGroupPagination: (pagination: Map<string, GroupPaginationState> | null) => void;
  updateGroupPagination: (
    updater: (current: Map<string, GroupPaginationState>) => Map<string, GroupPaginationState>
  ) => void;
  getGridGroupByFields: () => Array<string>;
  getSubGroupCounts: () => Record<string, number>;
  setSubGroupCounts: (counts: Record<string, number>) => void;
  addToBatchLoadQueue: (path: string) => void;
  takeBatchLoadQueueSnapshot: () => Array<string>;
  scheduleBatchFlush: (flush: () => void) => void;
  fetchGridPage: (where: Record<string, unknown>, after?: string) => Observable<GridRowsPage>;
  fetchNullBucketViaClientFilter: (
    path: string,
    bucketWhere: Record<string, unknown>,
    scanAfter: string | undefined,
    pageSize: number,
    expectedTotal: number,
    alreadyLoaded: number
  ) => Observable<GridRowsPage>;
  buildWhereFromPath: (path: string) => Record<string, unknown>;
  buildBucketInputFromPath: (path: string) => GridBucketScope;
  buildBatchBucketRequestOptions: (buckets: Array<GridBucketScope>) => {
    buckets: Array<GridBucketScope>;
    firstPerBucket: number;
    sharedWhere: Record<string, unknown>;
    attributeFilter?: Array<GridBackendAttributeFilter>;
    sort?: Array<GridSortField>;
  };
  isNullBucketPath: (path: string) => boolean;
  isEmptyElementsResponse: (response: GridRowsPage) => boolean;
  logEmptyNodeMismatch: (bucketKey: string, response: GridRowsPage) => void;
}

/** Fetches the next flat (ungrouped) page and appends it to `_elements`. */
export function loadNextFlatGridPage(host: GridPaginationHost): void {
  const cursor: string | null = host.getGridEndCursor();
  if (cursor === null || host.getGridLoadingMore()) {
    return;
  }

  const queryGeneration: number = host.getQueryGeneration();
  host.beginGridLoadingMore();

  host
    .fetchGridPage(host.getLastGridWhere(), cursor)
    .pipe(
      take(1),
      takeUntilDestroyed(host.destroyRef),
      catchError((err: Error): Observable<never> => {
        if (isStaleQueryGeneration(host, queryGeneration)) {
          host.endGridLoadingMore();
          return EMPTY;
        }
        host.setElementsError(err.message);
        logGridOrchestration(GridOrchestrationLogLevel.Error, 'Failed to load next grid page:', err);
        host.endGridLoadingMore();
        return EMPTY;
      }),
      tap((response: GridRowsPage): void => {
        if (isStaleQueryGeneration(host, queryGeneration)) {
          host.endGridLoadingMore();
          return;
        }
        const current: GridRowsPage | null = host.getElements();
        host.setElements({
          nodes: [...(current?.nodes ?? []), ...response.nodes],
          pageInfo: response.pageInfo,
          totalCount: current?.totalCount ?? response.totalCount
        });
        host.setGridEndCursor(response.pageInfo?.endCursor ?? null);
        host.setGridHasMore(response.pageInfo?.hasNextPage ?? false);
        host.endGridLoadingMore();
      })
    )
    .subscribe();
}

/** Queues a bucket path for batched loading (debounced flush). */
export function scheduleBucketLoad(host: GridPaginationHost, path: string): void {
  if (path === '') {
    return;
  }
  host.addToBatchLoadQueue(path);
  host.scheduleBatchFlush((): void => {
    flushBatchLoadQueue(host);
  });
}

/** Flushes queued bucket paths via the batch API when available. */
export function flushBatchLoadQueue(host: GridPaginationHost): void {
  const paths: Array<string> = host.takeBatchLoadQueueSnapshot();
  if (paths.length === 0) {
    return;
  }

  if (host.getGroupPagination() === null) {
    for (const path of paths) {
      logDeclinedBucketLoad(path, 'no pagination map exists yet', {});
    }
    return;
  }

  // The batch API only fetches page one per bucket. Buckets that already hold a
  // cursor must continue through `loadBucket`, otherwise they stall on page one.
  // `loadBucket` replaces the pagination map, so re-read it on every iteration.
  const loadablePaths: Array<string> = new Array<string>();
  for (const requestedPath of paths) {
    const pagination: Map<string, GroupPaginationState> | null = host.getGroupPagination();
    if (pagination === null) {
      break;
    }
    const path: string = resolveExistingBucketKey(requestedPath, pagination.keys()) ?? requestedPath;
    const state: GroupPaginationState | undefined = pagination.get(path);
    if (isBucketFirstPagePending(state)) {
      loadablePaths.push(path);
      continue;
    }
    if (isBucketLoadable(state)) {
      loadBucket(host, path);
      continue;
    }
    logDeclinedBucketLoad(path, 'the queued bucket is neither first-page pending nor loadable', {
      hasState: state !== undefined,
      hasNextPage: state?.hasNextPage,
      loading: state?.loading,
      pagesLoaded: state?.pagesLoaded,
      endCursor: state?.endCursor,
      cursorUsableForElements: state?.cursorUsableForElements,
      knownBucketKeys: Array.from(pagination.keys())
    });
  }

  if (loadablePaths.length === 0) {
    return;
  }

  const nullBucketPaths: Array<string> = loadablePaths.filter((path: string): boolean =>
    host.isNullBucketPath(path)
  );
  const batchablePaths: Array<string> = loadablePaths.filter(
    (path: string): boolean => !host.isNullBucketPath(path)
  );

  for (const path of nullBucketPaths) {
    loadBucket(host, path);
  }

  if (batchablePaths.length === 0) {
    return;
  }

  if (batchablePaths.length === 1) {
    loadBucket(host, batchablePaths[0]);
    return;
  }

  const batchPaths: Array<string> = batchablePaths.slice(0, BATCH_BUCKET_SIZE);
  const deferredPaths: Array<string> = batchablePaths.slice(BATCH_BUCKET_SIZE);
  const bucketInputs: Array<GridBucketScope> = batchPaths.map((path: string): GridBucketScope =>
    host.buildBucketInputFromPath(path)
  );
  const queryGeneration: number = host.getQueryGeneration();

  host.beginGridLoadingMore();
  for (const path of batchPaths) {
    markBucketLoading(host, path, true);
  }

  host.elementsProvider
    .getElementsForGroupBuckets(host.buildBatchBucketRequestOptions(bucketInputs))
    .pipe(
      take(1),
      takeUntilDestroyed(host.destroyRef),
      catchError((): Observable<Array<GridBucketRowsPage>> => {
        if (isStaleQueryGeneration(host, queryGeneration)) {
          for (const path of batchPaths) {
            markBucketLoading(host, path, false);
          }
          host.endGridLoadingMore();
          return EMPTY;
        }
        for (const path of batchPaths) {
          markBucketLoading(host, path, false);
        }
        host.endGridLoadingMore();
        batchPaths.forEach((path: string, index: number): void => {
          host.scheduleBatchFallbackLoad(path, index * BATCH_FLUSH_MS, queryGeneration);
        });
        if (deferredPaths.length > 0) {
          for (const path of deferredPaths) {
            scheduleBucketLoad(host, path);
          }
        }
        return EMPTY;
      }),
      tap((results: Array<GridBucketRowsPage>): void => {
        if (isStaleQueryGeneration(host, queryGeneration)) {
          for (const path of batchPaths) {
            markBucketLoading(host, path, false);
          }
          host.endGridLoadingMore();
          return;
        }
        for (const result of results) {
          applyBucketFetchResult(
            host,
            result.bucketKey,
            {
              nodes: result.nodes,
              totalCount: result.totalCount,
              pageInfo: result.pageInfo
            },
            { cursorSource: 'batch' }
          );
        }
        mergeGroupPaginationIntoElements(host);
        host.endGridLoadingMore();
        updateGlobalHasMoreFromPagination(host);
        if (deferredPaths.length > 0) {
          for (const path of deferredPaths) {
            scheduleBucketLoad(host, path);
          }
        }
      })
    )
    .subscribe();
}

/**
 * Fetches an `elements` page for a bucket.
 *
 * Batch first pages store an element-key cursor that HotChocolate `elements.after` cannot decode,
 * so the first continuation has to restart from page one with no cursor. What that page is worth
 * depends on how it lines up with the rows already held, and both outcomes have to be handled:
 * an `elements` page is larger than a batch page, so it usually carries rows the batch page never
 * returned and must be kept — discarding it stranded buckets smaller than one `elements` page on
 * their batch rows forever. But when it repeats rows already held it adds nothing, and returning
 * it would burn the scroll that asked for more without advancing, so this skips to the next page.
 *
 * @param host - Pagination host.
 * @param bucketWhere - Where clause for the bucket (no remapped group keys).
 * @param after - HotChocolate cursor, or undefined for the first `elements` page.
 * @param heldIds - Ids already loaded for this bucket.
 * @returns Observable of the page to apply to bucket state.
 */
function fetchElementsPageForBucket(
  host: GridPaginationHost,
  bucketWhere: Record<string, unknown>,
  after: string | undefined,
  heldIds: ReadonlySet<string>
): Observable<GridRowsPage> {
  if (after !== undefined) {
    return host.fetchGridPage(bucketWhere, after);
  }

  return host.fetchGridPage(bucketWhere).pipe(
    switchMap((pageOne: GridRowsPage): Observable<GridRowsPage> => {
      const endCursor: string | null | undefined = pageOne.pageInfo?.endCursor;
      const addsRows: boolean = pageOne.nodes.some((node: GridRowsPage['nodes'][number]): boolean => {
        const id: string | undefined = asGridSourceRow(node).id;
        return id === undefined || !heldIds.has(id);
      });
      if (
        addsRows ||
        !(pageOne.pageInfo?.hasNextPage ?? false) ||
        endCursor === null ||
        endCursor === undefined
      ) {
        return of(pageOne);
      }
      return host.fetchGridPage(bucketWhere, endCursor);
    })
  );
}

/**
 * Collects the ids a bucket already holds, used to tell a useful restart page from a repeat.
 *
 * @param state - Current bucket state.
 * @returns Set of loaded row ids.
 */
function heldBucketNodeIds(state: GroupPaginationState): ReadonlySet<string> {
  const ids: Set<string> = new Set<string>();
  for (const node of state.nodes) {
    const id: string | undefined = asGridSourceRow(node).id;
    if (id !== undefined) {
      ids.add(id);
    }
  }
  return ids;
}

/**
 * Records that a load-more request was dropped, and why.
 *
 * Every guard below returns silently, so a leaf that stops paginating looks identical whether the
 * scroll never fired, the path was spelled differently to its pagination key, or the bucket
 * genuinely had nothing left. Naming the guard is the difference between reading the answer off
 * the console and inferring it.
 *
 * @param path - Bucket path that was asked to load.
 * @param reason - Which guard declined.
 * @param details - Values that guard looked at.
 */
function logDeclinedBucketLoad(path: string, reason: string, details: Record<string, unknown>): void {
  logGridOrchestration(
    GridOrchestrationLogLevel.Warn,
    `[GridDiag] Load-more for bucket '${path}' declined: ${reason}.`,
    { bucketPath: path, ...details }
  );
}

/** Loads the next page for a single bucket and merges into `_elements`. */
export function loadBucket(host: GridPaginationHost, requestedPath: string): void {
  const fields: Array<string> = host.getGridGroupByFields();
  if (fields.length === 0 || requestedPath === '') {
    logDeclinedBucketLoad(requestedPath, 'the grid is not grouped, or the path is empty', {
      groupByFields: fields
    });
    return;
  }

  const pagination: Map<string, GroupPaginationState> | null = host.getGroupPagination();
  if (pagination === null) {
    logDeclinedBucketLoad(requestedPath, 'no pagination map exists yet', {});
    return;
  }

  // Callers reach here holding whichever spelling their source used, so the bucket is identified
  // by what pagination stores rather than by an exact string match.
  const path: string = resolveExistingBucketKey(requestedPath, pagination.keys()) ?? requestedPath;
  const state: GroupPaginationState | undefined = pagination.get(path);
  if (state === undefined && fields.length <= 1) {
    logDeclinedBucketLoad(requestedPath, 'no bucket state under this key at a single grouping level', {
      knownBucketKeys: Array.from(pagination.keys())
    });
    return;
  }
  const bucketState: GroupPaginationState = state ?? createBucketSkeleton(host, path);
  if (!bucketState.hasNextPage || bucketState.loading) {
    logDeclinedBucketLoad(path, 'the bucket reports no next page, or is already loading', {
      hasNextPage: bucketState.hasNextPage,
      loading: bucketState.loading,
      loadedRows: bucketState.nodes.length,
      totalCount: bucketState.totalCount
    });
    return;
  }
  if (
    bucketState.pagesLoaded > 0 &&
    bucketState.endCursor === null &&
    bucketState.cursorUsableForElements !== false
  ) {
    logDeclinedBucketLoad(path, 'the bucket has pages left but no cursor to continue from', {
      pagesLoaded: bucketState.pagesLoaded,
      loadedRows: bucketState.nodes.length,
      totalCount: bucketState.totalCount
    });
    return;
  }

  markBucketLoading(host, path, true);
  const after: string | undefined =
    bucketState.pagesLoaded > 0 && bucketState.cursorUsableForElements !== false
      ? (bucketState.endCursor ?? undefined)
      : undefined;
  const restartElementsWithoutCursor: boolean =
    bucketState.pagesLoaded > 0 && bucketState.cursorUsableForElements === false;
  const bucketWhere: Record<string, unknown> = host.buildWhereFromPath(path);
  const expectedTotal: number = resolveExpectedBucketTotal(
    path,
    bucketState.totalCount,
    host.getSubGroupCounts()
  );
  const alreadyLoaded: number = bucketState.nodes.length;

  const fetch$: Observable<GridRowsPage> = bucketState.useClientNullFilter
    ? host.fetchNullBucketViaClientFilter(
        path,
        bucketWhere,
        bucketState.nullScanCursor ?? undefined,
        BUCKET_PAGE_SIZE,
        expectedTotal,
        alreadyLoaded
      )
    : fetchElementsPageForBucket(host, bucketWhere, after, heldBucketNodeIds(bucketState)).pipe(
        switchMap((response: GridRowsPage): Observable<GridRowsPage> => {
          if (
            after !== undefined ||
            restartElementsWithoutCursor ||
            !host.isEmptyElementsResponse(response) ||
            !host.isNullBucketPath(path) ||
            expectedTotal <= 0
          ) {
            return of(response);
          }
          return host.fetchNullBucketViaClientFilter(
            path,
            bucketWhere,
            undefined,
            BUCKET_PAGE_SIZE,
            expectedTotal,
            alreadyLoaded
          );
        })
      );

  const queryGeneration: number = host.getQueryGeneration();
  host.beginGridLoadingMore();
  fetch$
    .pipe(
      take(1),
      takeUntilDestroyed(host.destroyRef),
      catchError((err: Error): Observable<never> => {
        if (isStaleQueryGeneration(host, queryGeneration)) {
          markBucketLoading(host, path, false);
          host.endGridLoadingMore();
          return EMPTY;
        }
        logGridOrchestration(
          GridOrchestrationLogLevel.Error,
          `Failed to load page for bucket '${path}':`,
          err
        );
        markBucketLoading(host, path, false);
        host.endGridLoadingMore();
        return EMPTY;
      }),
      tap((response: GridRowsPage): void => {
        if (isStaleQueryGeneration(host, queryGeneration)) {
          markBucketLoading(host, path, false);
          host.endGridLoadingMore();
          return;
        }
        host.logEmptyNodeMismatch(path, response);
        applyBucketFetchResult(host, path, response, {
          clientNullFilter:
            bucketState.useClientNullFilter ||
            (host.isNullBucketPath(path) &&
              response.nodes.length > 0 &&
              (response.totalCount ?? 0) > 0 &&
              expectedTotal > 0),
          cursorSource: 'elements'
        });
        mergeGroupPaginationIntoElements(host);
        host.endGridLoadingMore();
        updateGlobalHasMoreFromPagination(host);
      })
    )
    .subscribe();
}

/** Immutably updates a single group's pagination state on the host. */
export function updateGroupPaginationState(
  host: GridPaginationHost,
  groupValue: string,
  updated: GroupPaginationState
): void {
  host.updateGroupPagination(
    (current: Map<string, GroupPaginationState>): Map<string, GroupPaginationState> =>
      immutableUpdateGroupPaginationState(current, groupValue, updated)
  );
}

/** Merges all per-group pagination nodes into the single `elements` signal. */
export function mergeGroupPaginationIntoElements(host: GridPaginationHost): void {
  const pagination: Map<string, GroupPaginationState> | null = host.getGroupPagination();
  if (pagination === null) {
    return;
  }

  const merged: MergedPaginationElements = mergeGroupPaginationIntoElementsData(pagination);
  host.setElements({ nodes: merged.nodes, totalCount: merged.totalCount });
  host.setGridHasMore(merged.hasMore);
}

/** Updates `_gridHasMore` from per-bucket pagination state. */
export function updateGlobalHasMoreFromPagination(host: GridPaginationHost): void {
  const pagination: Map<string, GroupPaginationState> | null = host.getGroupPagination();
  if (pagination === null) {
    return;
  }
  host.setGridHasMore(computeGlobalHasMoreFromPagination(pagination));
}

/** Creates a default skeleton pagination entry for a path not yet in the map. */
export function createBucketSkeleton(host: GridPaginationHost, path: string): GroupPaginationState {
  const totalCount: number = resolveExpectedBucketTotal(path, 0, host.getSubGroupCounts());
  const skeleton: GroupPaginationState = createBucketSkeletonState(path, totalCount);
  if (host.getGroupPagination() !== null) {
    updateGroupPaginationState(host, path, skeleton);
  }
  return skeleton;
}

/**
 * Marks a bucket as loading / not loading in `_groupPagination`.
 *
 * A bucket expanded for the first time has no pagination entry yet, so marking it loading has to
 * seed one: without it the flag is dropped and the group renders as idle for the whole fetch.
 * Clearing the flag stays a no-op for an unknown path, since there is no progress to report.
 */
export function markBucketLoading(host: GridPaginationHost, path: string, loading: boolean): void {
  const seedTotal: number = loading ? resolveExpectedBucketTotal(path, 0, host.getSubGroupCounts()) : 0;

  host.updateGroupPagination(
    (current: Map<string, GroupPaginationState>): Map<string, GroupPaginationState> => {
      const state: GroupPaginationState | undefined = current.get(path);
      if (state === undefined) {
        if (!loading) {
          return current;
        }
        return immutableUpdateGroupPaginationState(current, path, {
          ...createBucketSkeletonState(path, seedTotal),
          loading: true
        });
      }
      return immutableUpdateGroupPaginationState(current, path, { ...state, loading });
    }
  );
}

/** Applies a fetch result to a bucket's pagination state. */
export function applyBucketFetchResult(
  host: GridPaginationHost,
  path: string,
  response: GridRowsPage,
  options?: { clientNullFilter?: boolean; cursorSource?: 'batch' | 'elements' }
): void {
  if (host.getGroupPagination() === null) {
    return;
  }

  // Only `elementsForGroupBuckets` reports a total scoped to the requested bucket; an `elements`
  // continuation reports the count for the whole query and would overstate the group.
  if (options?.cursorSource === 'batch') {
    const nextCounts: Record<string, number> | null = recordBucketTotalAsSubGroupCount(
      path,
      response.totalCount ?? 0,
      host.getSubGroupCounts()
    );
    if (nextCounts !== null) {
      host.setSubGroupCounts(nextCounts);
    }
  }

  host.updateGroupPagination(
    (current: Map<string, GroupPaginationState>): Map<string, GroupPaginationState> => {
      const existingState: GroupPaginationState | undefined = current.get(path);
      const state: GroupPaginationState =
        existingState ??
        createBucketSkeletonState(path, resolveExpectedBucketTotal(path, 0, host.getSubGroupCounts()));
      const resolvedTotal: number = resolveExpectedBucketTotal(
        path,
        state.totalCount,
        host.getSubGroupCounts()
      );
      const updated: GroupPaginationState = computeNextBucketStateAfterFetch(
        path,
        state,
        response,
        resolvedTotal,
        options
      );
      return immutableUpdateGroupPaginationState(current, path, updated);
    }
  );
}

/** Whether a nested composite path uses the group path delimiter. */
export function isNestedBucketPath(path: string): boolean {
  return path.includes(GROUP_PATH_DELIMITER);
}

/** Partitions queued paths into null-bucket and batchable sets. */
export function partitionLoadablePaths(
  paths: ReadonlyArray<string>,
  groupByFields: ReadonlyArray<string>
): { nullBucketPaths: Array<string>; batchablePaths: Array<string> } {
  const nullBucketPaths: Array<string> = paths.filter((path: string): boolean =>
    isNullBucketPath(path, groupByFields)
  );
  const batchablePaths: Array<string> = paths.filter(
    (path: string): boolean => !isNullBucketPath(path, groupByFields)
  );
  return { nullBucketPaths, batchablePaths };
}
